/**
 * Draws the Google maps saved by kaplan/map and kaplan/map-locations-map.
 *
 * Adapted from Getwid 3.0.2's map view script (GPL-2.0-or-later). Maps are
 * found by Getwid's class names, which the saved markup keeps.
 */
import { getMapStyles } from '../../assets/src/components/google-map/utils';

const BASE_CLASS = 'wp-block-getwid-map';

/**
 * Decode HTML entities without interpreting the markup.
 *
 * @param {string} value Encoded text.
 * @returns {string} Decoded text.
 */
function decodeEntities( value ) {
	const textarea = document.createElement( 'textarea' );
	textarea.innerHTML = value;
	return textarea.value;
}

/**
 * Remove scripts, event handlers and script URLs from marker popup HTML.
 *
 * Parsed in an inert document, so nothing in the markup runs while it is
 * cleaned.
 *
 * @param {string} html Popup HTML.
 * @returns {string} Cleaned HTML.
 */
function sanitizeHtml( html ) {
	const doc = document.implementation.createHTMLDocument( '' );
	doc.body.innerHTML = html;

	doc.body.querySelectorAll( 'script, style, iframe, object, embed' ).forEach( node => node.remove() );
	doc.body.querySelectorAll( '*' ).forEach( node => {
		Array.from( node.attributes ).forEach( ( { name, value } ) => {
			if ( name.startsWith( 'on' ) || /^\s*(javascript|data|vbscript):/i.test( value ) ) {
				node.removeAttribute( name );
			}
		} );
	} );

	return doc.body.innerHTML;
}

/**
 * Read a boolean data attribute as Getwid saved it.
 *
 * @param {HTMLElement} element Map element.
 * @param {string}      name    Attribute name.
 * @returns {boolean} Value.
 */
function readBoolean( element, name ) {
	return element.getAttribute( name ) !== 'false';
}

/**
 * Parse JSON without throwing.
 *
 * @param {string|null} value JSON string.
 * @param {*}           fallback Value to return when parsing fails.
 * @returns {*} Parsed value.
 */
function readJson( value, fallback ) {
	try {
		return value ? JSON.parse( value ) : fallback;
	} catch ( error ) {
		return fallback;
	}
}

/**
 * Draw one map.
 *
 * @param {HTMLElement} element Map element.
 * @param {object}      google  Google Maps namespace.
 */
function drawMap( element, google ) {
	const container = element.querySelector( `.${ BASE_CLASS }__container` );

	if ( ! container ) {
		return;
	}

	element.classList.add( 'getwid-init' );
	element.querySelector( `.${ BASE_CLASS }__points` )?.remove();

	// Read as strings: the custom style parser expects the JSON as saved.
	const markers = readJson( element.getAttribute( 'data-map-markers' ), [] );
	const interaction = element.getAttribute( 'data-interaction' );

	const map = new google.maps.Map( container, {
		center: readJson( element.getAttribute( 'data-map-center' ), {
			lat: 0,
			lng: 0,
		} ),
		styles: getMapStyles( element.getAttribute( 'data-map-style' ), element.getAttribute( 'data-custom-style' ) ),
		gestureHandling: [ 'cooperative', 'greedy', 'none', 'auto' ].includes( interaction ) ? interaction : 'auto',
		zoomControl: readBoolean( element, 'data-zoom-control' ),
		mapTypeControl: readBoolean( element, 'data-type-control' ),
		streetViewControl: readBoolean( element, 'data-street-view-control' ),
		fullscreenControl: readBoolean( element, 'data-full-screen-control' ),
		zoom: Number( element.getAttribute( 'data-map-zoom' ) ) || 2,
	} );

	( Array.isArray( markers ) ? markers : [] ).forEach( markerData => {
		const marker = new google.maps.Marker( {
			position: markerData.coords,
			map,
			draggable: false,
			animation: google.maps.Animation.DROP,
		} );

		if ( markerData.bounce ) {
			setTimeout( () => marker.setAnimation( google.maps.Animation.BOUNCE ), 2000 );
		}

		const description = sanitizeHtml( decodeEntities( markerData.description || '' ) );

		if ( description === '' ) {
			return;
		}

		const popUp = new google.maps.InfoWindow( {
			content: `<div class="getwid-poi-info-window">${ description }</div>`,
			maxWidth: Number( markerData.popUpMaxWidth ) || undefined,
		} );

		if ( markerData.popUpOpen ) {
			popUp.open( map, marker );
		}

		marker.addListener( 'click', () => popUp.open( map, marker ) );
	} );
}

/**
 * Draw every map on the page that has not been drawn yet.
 */
function drawMaps() {
	const { google } = window;

	if ( ! google?.maps ) {
		return;
	}

	document.querySelectorAll( `.${ BASE_CLASS }:not(.getwid-init)` ).forEach( element => drawMap( element, google ) );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', drawMaps );
} else {
	drawMaps();
}
